import { createMailSearchTableQuery } from 'owa-mail-list-search';
import getSearchQueryString from '../selectors/getSearchQueryString';
import getSearchScopeListViewType from '../utils/getSearchScopeListViewType';
import getFolderNameFromScope from '../utils/getFolderNameFromScope';
import shouldStartSearch from '../utils/shouldStartSearch';
import mailSearchStore from '../store/store';
import setSearchNumber from '../mutators/setSearchNumber';
import { is3SServiceAvailable } from 'owa-search';
import { ActionSource } from 'owa-mail-store';
import { addToLocalSearchHistory } from '../legacyActions/searchHistory';
import { getListViewTypeString } from 'owa-mail-list-store';
import { loadTableViewFromSearchTableQuery } from '../actions/publicActions';
import { PerformanceDatapoint } from 'owa-analytics';
import { lazyResetFocus } from 'owa-mail-focus-manager';
import {
    getScenarioStore,
    lazyGetSuggestionAtIndex,
    startSearchSession,
    SearchScenarioId,
    startSearch,
    SearchProvider,
} from 'owa-search';
import { SuggestionKind, SubstrateSearchScenario } from 'owa-search-service';
import {
    lazyLogSearchDone,
    lazyLogEntityClicked,
    lazyLogSearchEntityActions,
    lazyLogSearchActions,
} from 'owa-search-instrumentation';
import isAttachmentsRefinerApplied from '../selectors/isAttachmentsRefinerApplied';
import isDateRefinerApplied from '../selectors/isDateRefinerApplied';
import { getDateRange } from '../datapoints';
import { getAggregationBucket } from 'owa-analytics-actions';
import { createLazyOrchestrator } from 'owa-bundling';
import { isFeatureEnabled } from 'owa-feature-flags';

export const startSearchOrchestrator = createLazyOrchestrator(
    startSearch,
    'CLONE_START_SEARCH',
    async actionMessage => {
        const actionSource = actionMessage.actionSource as ActionSource;
        const explicitSearch = actionMessage.explicitSearch;
        const searchProvider = actionMessage.searchProvider as SearchProvider;
        const searchStore = getScenarioStore(SearchScenarioId.Mail);

        if (!shouldStartSearch(actionMessage.scenarioId, actionSource)) {
            return;
        }

        /**
         * If user has initiated a search without being in a search session (i.e. clicking a
         * category tag), then we should bail and start a search session first.
         */
        if (!searchStore.searchSessionGuid) {
            startSearchSession(actionSource, true /* shouldStartSearch */, SearchScenarioId.Mail);
            return;
        }

        // Since search is sure to happen at this point, create performance datapoint.
        const searchEndToEndDatapoint = new PerformanceDatapoint('SearchEndToEnd', { isQos: true });

        /**
         * Log event if search was explicit (i.e. "Enter" in input or clicking the
         * search button, as opposed to clicking a suggestion).
         */
        if (explicitSearch && is3SServiceAvailable()) {
            const selectedSuggestionIndex = searchStore.selectedSuggestionIndex;

            /**
             * If selectedSuggestionIndex is non-default, log an entityclicked event
             * because the suggestion led the user to search without them modifying
             * the query.
             *
             * Else, log a searchdone event indicating a search was done as a result
             * of the user entering the query themselves without choosing a suggestion.
             */
            if (selectedSuggestionIndex > -1) {
                const getSuggestionAtIndex = await lazyGetSuggestionAtIndex.import();

                // Get the suggestion from the search store.
                const suggestion = getSuggestionAtIndex(
                    selectedSuggestionIndex,
                    SearchScenarioId.Mail
                );

                // Get trace ID from suggestion set.
                const suggestionSet = getScenarioStore(SearchScenarioId.Mail).currentSuggestions;
                const suggestionSetTraceId = suggestionSet?.TraceId;

                const logicalId = searchStore.traceIdToLogicalIdMap.get(suggestionSetTraceId);

                /**
                 * This check ensures we're only logging an entityclicked event for
                 * a suggestion provided by QF.
                 */
                if (
                    suggestion.kind === SuggestionKind.People
                        ? suggestion.Source === 'qf'
                        : suggestionSetTraceId
                ) {
                    if (isFeatureEnabled('sea-instv2')) {
                        lazyLogSearchEntityActions.importAndExecute(
                            SubstrateSearchScenario.Mail,
                            null,
                            null,
                            logicalId,
                            suggestionSetTraceId,
                            suggestion.ReferenceId,
                            'EntityClicked'
                        );
                    } else {
                        lazyLogEntityClicked.importAndExecute(
                            suggestionSetTraceId,
                            suggestion.ReferenceId,
                            new Date(),
                            SubstrateSearchScenario.Mail
                        );
                    }
                }
            } else {
                const emptyGuid = '00000000-0000-0000-0000-000000000000';

                /**
                 * According to 3S spec for "searchdone" event (4/19/2018), the trace ID
                 * should be:
                 * - ID associated with last rendered QF request OR
                 * - ID associated with last QF request issued OR
                 * - An empty GUID
                 */
                const latestQfTraceId =
                    searchStore.latestRenderedQFTraceId ||
                    searchStore.latestQFTraceId ||
                    searchStore.latestQFRequestId ||
                    emptyGuid;
                if (isFeatureEnabled('sea-instv2')) {
                    const logicalId = searchStore.traceIdToLogicalIdMap.get(latestQfTraceId);
                    lazyLogSearchActions.importAndExecute(
                        SubstrateSearchScenario.Mail,
                        null,
                        null,
                        logicalId,
                        latestQfTraceId,
                        'SearchDone'
                    );
                } else {
                    lazyLogSearchDone.importAndExecute(
                        latestQfTraceId,
                        new Date(),
                        SubstrateSearchScenario.Mail
                    );
                }
            }
        }

        const appliedFilter = actionMessage.filter;
        // Build query string from search box contents.
        const queryString = getSearchQueryString(appliedFilter);

        // Increment search number and add query string to local history.
        setSearchNumber(mailSearchStore.searchNumber + 1);

        if (searchStore.suggestionPillIds.length <= 0) {
            addToLocalSearchHistory(queryString);
        }

        const listViewType = getSearchScopeListViewType(mailSearchStore.staticSearchScope);
        const searchTableQuery = createMailSearchTableQuery(
            mailSearchStore.searchNumber,
            queryString,
            mailSearchStore.staticSearchScope,
            searchStore.suggestionPills,
            listViewType,
            mailSearchStore.includeAttachments,
            mailSearchStore.fromDate,
            mailSearchStore.toDate,
            searchProvider,
            appliedFilter
        );

        // Determine number of refiners applied.
        let appliedRefinersCount = 0;
        isAttachmentsRefinerApplied() ? appliedRefinersCount++ : null;
        isDateRefinerApplied() ? appliedRefinersCount++ : null;

        // get the foldername to add to property bag of datapoint.
        const folderName: string = getFolderNameFromScope(mailSearchStore.staticSearchScope);

        // Add custom data to SearchEndToEnd datapoint.
        searchEndToEndDatapoint.addCustomData({
            actionSource: actionSource,
            searchTextLength: getAggregationBucket({ value: searchStore.searchText.length }),
            suggestionPillsLength: getAggregationBucket({
                value: searchStore.suggestionPillIds.length,
            }),
            folderName: folderName,
            listViewType: getListViewTypeString(listViewType),
            searchScope: mailSearchStore.staticSearchScope.kind,
            appliedRefinersCount: appliedRefinersCount,
            dateRange: getDateRange(mailSearchStore.fromDate, mailSearchStore.toDate),
            queryIncludesKql: queryIncludesKql(searchStore.searchText),
        });

        loadTableViewFromSearchTableQuery(searchTableQuery, searchEndToEndDatapoint, actionSource);

        /**
         * Reset focus after search kicks off so focus is given to the list view
         * and user can interact with search results.
         */
        lazyResetFocus.importAndExecute();
    }
);

const queryIncludesKql = (query: string): boolean => {
    return (
        query.indexOf('from:') > -1 ||
        query.indexOf('to:') > -1 ||
        query.indexOf('subject:') > -1 ||
        query.indexOf('received>') > -1 ||
        query.indexOf('received<') > -1 ||
        query.indexOf('received=') > -1
    );
};
